import React, { useState, useEffect, useRef } from 'react';
import useAuthStore from '../../../store/authStore';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import useSetOnlineStatus from '../../../hooks/useSetOnlineStatus';
import Sidebar from './Sidebar';
import ChatPanel from '../ChatPanel';
import { db } from '../../../firebase';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

// Error boundary for Firebase errors
class FirebaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Don't log the full error to avoid spam
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-center text-gray-500">
          <p>Chat temporarily unavailable</p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 text-blue-500 hover:text-blue-700"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const ChatAppPopup = ({ isOpen, onClose, conversationId = null }) => {
  useSetOnlineStatus();
  const { user, isAdmin } = useAuthStore();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [agentMessages, setAgentMessages] = useState([]);
  const [agentConversations, setAgentConversations] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [directConversationLoading, setDirectConversationLoading] = useState(false);
  const { isDarkMode } = useDarkMode();
  const suppressAutoOpenRef = useRef(false);

  // Ensure home state on popup open; only suppress auto-open when no conversationId provided
  useEffect(() => {
    if (isOpen) {
      const hasTarget = !!(conversationId && conversationId.trim() !== '');
      suppressAutoOpenRef.current = !hasTarget;
      setSelectedConversation(null);
      setActiveTab('all');
      setDirectConversationLoading(false);
    }
  }, [isOpen, conversationId]);

  // Lock body scroll while popup is open, restore on close
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  // Handler to select a conversation (no navigation - stay in popup)
  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
  };

  // Effect to handle direct conversation selection from props
  useEffect(() => {
    if (suppressAutoOpenRef.current) {
      // Skip auto-opening a conversation the first time the popup is opened
      suppressAutoOpenRef.current = false;
      return;
    }

    if (conversationId && isOpen && conversationId.trim() !== '') {
      // First try to find in existing transactions
      if (Array.isArray(transactions) && transactions.length > 0) {
        const targetConversation = transactions.find(conv => conv && conv.id === conversationId);
        if (targetConversation) {
          setSelectedConversation(targetConversation);
          return;
        }
      }
      
      // If not found in transactions, fetch it directly from Firestore
      const fetchConversationDirectly = async () => {
        try {
          setDirectConversationLoading(true);
          const docRef = doc(db, 'sellerbuyerconversations', conversationId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const convData = { id: docSnap.id, ...docSnap.data() };
            const formattedConversation = {
              ...convData,
              name: convData.seller?.name || convData.seller_name || 'Seller',
              avatar: convData.seller_avatar || convData.seller?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(convData.seller?.name || convData.seller_name || 'Seller')}`,
              date: convData.order_date || (convData.created_at && convData.created_at.toDate ? convData.created_at.toDate().toLocaleDateString() : ''),
              unreadCount: 0
            };
            
            setSelectedConversation(formattedConversation);
            
            // Add to transactions list if not already there
            setTransactions(prev => {
              if (Array.isArray(prev) && !prev.find(conv => conv && conv.id === conversationId)) {
                return [prev[0], formattedConversation, ...prev.slice(1)];
              }
              return prev;
            });
          } else {
            toast.error('Conversation not found. Please try again.');
          }
        } catch (error) {
          toast.error('Failed to load conversation. Please try again.');
        } finally {
          setDirectConversationLoading(false);
        }
      };
      
      fetchConversationDirectly();
    }
  }, [conversationId, isOpen, transactions]);

  // Monitor activeTab changes

  // Fetch transaction conversations from Firestore
  useEffect(() => {
    if (!user || !user.email) return; // Add email check
    setSidebarLoading(true);
    
    try {
      const q = query(
        collection(db, 'sellerbuyerconversations'),
        where('type', '==', 'transaction'),
        where('members', 'array-contains', user.email)
      );
      const unsub = onSnapshot(q, (snapshot) => {
        const txs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Add agent chat at the top (placeholder)
        const allTransactions = [
          {
            id: 'admin',
            name: 'Chat with Agent',
            date: new Date().toLocaleDateString(),
            online: true,
            avatar: 'https://i.pravatar.cc/40?img=1',
            lastMessage: 'How can I help you today?',
            unreadCount: 0,
            isAdmin: true
          },
          ...txs.map(conv => ({
            ...conv,
            name: conv.seller?.name || conv.seller_name || 'Seller',
            avatar: conv.seller_avatar || conv.seller?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conv.seller?.name || conv.seller_name || 'Seller')}`,
            date: conv.order_date || (conv.created_at && conv.created_at.toDate ? conv.created_at.toDate().toLocaleDateString() : ''),
            unreadCount: 0
          }))
        ];
        setTransactions(allTransactions);
        setSidebarLoading(false);
      }, (error) => {
        setSidebarLoading(false);
      });
      return () => unsub();
    } catch (error) {
      setSidebarLoading(false);
    }
  }, [user]);

  // Fetch agent conversations from Firestore
  useEffect(() => {
    if (!user || !user.email) return; // Add email check
    
    try {
      const q = query(
        collection(db, 'agentConversations'),
        orderBy('createdAt', 'desc')
      );
      
      const unsub = onSnapshot(q, (snapshot) => {
        const agentConvs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          name: doc.data().userName || doc.data().user?.name || 'Agent Chat',
          avatar: doc.data().user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(doc.data().userName || 'Agent')}`,
          date: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate().toLocaleDateString() : new Date().toLocaleDateString(),
          lastMessage: doc.data().lastMessage || 'Start a conversation',
          unreadCount: doc.data().unreadCount || 0,
          isAdmin: true
        }));
        
        setAgentConversations(agentConvs);
      }, (error) => {
      });
      
      return () => unsub();
    } catch (error) {
    }
  }, [user]);

  if (!isOpen) return null;

  // Additional safety check
  if (!Array.isArray(transactions)) {
    return null;
  }

  return (
    <FirebaseErrorBoundary>
      <div className="fixed inset-0 z-50 overscroll-none">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Chat Container - Full Screen */}
        <div className={`relative w-full h-full overflow-hidden ${
          isDarkMode ? 'bg-[#0f0f0f] text-white' : 'bg-white text-gray-900'
        }`}>
         
          {/* Close Button - Floating */}
          <button
            onClick={onClose}
            className={`absolute top-6 right-4 z-50 p-2 sm:p-3 rounded-full hover:bg-red-600 transition-colors flex-shrink-0 bg-red-500 text-white shadow-lg`}
            title="Close chat"
          >
            <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>


          {/* Floating chat bubble buttons around screen - Only show on home page in chat panel area, hidden on mobile/tablet */}
          {!selectedConversation && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden hidden lg:block">
              {/* Top left floating button - View orders */}
              <div className="absolute top-24 left-1/4 sm:left-1/3 opacity-80">
                <div className={`${
                  isDarkMode 
                    ? 'bg-[#005C4B] text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow'
                } rounded-2xl rounded-br-sm px-4 py-2 flex items-center space-x-2 cursor-pointer hover:shadow-xl transition-shadow duration-200`}>
                  {/* Avatar icon */}
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M16 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 2H9C8.44772 2 8 2.44772 8 3V5C8 5.55228 8.44772 6 9 6H15C15.5523 6 16 5.55228 16 5V3C16 2.44772 15.5523 2 15 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">View your orders</span>
                </div>
              </div>
              
              {/* Top right floating button - Start new conversation */}
              <div className="absolute top-24 right-8 opacity-80">
                <div className={`${
                  isDarkMode 
                    ? 'bg-[#005C4B] text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow'
                } rounded-2xl rounded-br-sm px-4 py-2 flex items-center space-x-2 cursor-pointer hover:shadow-xl transition-shadow duration-200`}>
                  {/* Avatar icon */}
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Start a new conversation</span>
                </div>
              </div>
              
              {/* Middle left floating button - Support team */}
              <div className="absolute top-1/2 left-1/4 sm:left-1/3 transform -translate-y-1/2 opacity-80">
                <div className={`${
                  isDarkMode 
                    ? 'bg-[#005C4B] text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow'
                } rounded-2xl rounded-br-sm px-4 py-2 flex items-center space-x-2 cursor-pointer hover:shadow-xl transition-shadow duration-200`}>
                  {/* Avatar icon */}
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Chat with our support team</span>
                </div>
              </div>
              
              {/* Middle right floating button - Security protection */}
              <div className="absolute top-1/2 right-8 transform -translate-y-1/2 opacity-80">
                <div className={`${
                  isDarkMode 
                    ? 'bg-[#005C4B] text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow'
                } rounded-2xl rounded-br-sm px-4 py-2 flex items-center space-x-2 cursor-pointer hover:shadow-xl transition-shadow duration-200`}>
                  {/* Avatar icon - Shield/Security */}
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">You're protected — admins oversee all chats</span>
                </div>
              </div>
              
              {/* Bottom left floating button - Browse transactions */}
              <div className="absolute bottom-24 left-1/4 sm:left-1/3 opacity-80">
                <div className={`${
                  isDarkMode 
                    ? 'bg-[#005C4B] text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow'
                } rounded-2xl rounded-br-sm px-4 py-2 flex items-center space-x-2 cursor-pointer hover:shadow-xl transition-shadow duration-200`}>
                  {/* Avatar icon */}
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M9 12H9.01M12 12H12.01M15 12H15.01M21 12C21 16.9706 16.9706 21 12 21C10.4876 21 9.01589 20.6888 7.65947 20.1012L3 21L3.8988 16.3405C3.3112 14.9841 3 13.5124 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Browse your transactions</span>
                </div>
              </div>
              
              {/* Bottom right floating button - Check messages */}
              <div className="absolute bottom-24 right-8 opacity-80">
                <div className={`${
                  isDarkMode 
                    ? 'bg-[#005C4B] text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow'
                } rounded-2xl rounded-br-sm px-4 py-2 flex items-center space-x-2 cursor-pointer hover:shadow-xl transition-shadow duration-200`}>
                  {/* Avatar icon */}
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M9 12H9.01M12 12H12.01M15 12H15.01M21 12C21 16.9706 16.9706 21 12 21C10.4876 21 9.01589 20.6888 7.65947 20.1012L3 21L3.8988 16.3405C3.3112 14.9841 3 13.5124 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Check your messages</span>
                </div>
              </div>
            </div>
          )}

          {/* Chat App Content - Full Mobile Integration */}
          <div className="flex h-full">
            {/* Sidebar - Full mobile support */}
            <div className={`${selectedConversation ? 'hidden sm:flex' : 'flex'} flex-shrink-0 w-full sm:w-auto`}>
              <Sidebar
                transactions={transactions}
                messages={messages}
                onSelectConversation={handleSelectConversation}
                selectedConversationId={selectedConversation?.id}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarWidth={490}
                showAgentTab={false}
                showSampleAgentCard={false}
                agentConversations={agentConversations}
                sidebarLoading={sidebarLoading}
                isAdmin={isAdmin}
              />
            </div>

            {/* Chat Panel - Full mobile support with back navigation */}
            <div className={`${selectedConversation ? 'flex' : 'hidden sm:flex'} flex-1 flex-col min-w-0 overflow-y-auto`}>
              {selectedConversation ? (
                <ChatPanel
                  conversation={selectedConversation}
                  activeTab={activeTab}
                  onSelectConversation={handleSelectConversation}
                  transactions={transactions}
                  agentMessages={agentMessages}
                  setAgentMessages={setAgentMessages}
                  messages={messages}
                  setMessages={setMessages}
                  selectedConversationId={selectedConversation?.id}
                />
              ) : directConversationLoading ? (
                <div className={`flex-1 flex items-center justify-center ${
                  isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
                }`}>
                  <div className="text-center px-4 max-w-sm">
                    <div className={`text-4xl mb-4 animate-spin ${
                      isDarkMode ? 'text-gray-600' : 'text-gray-300'
                    }`}>
                      ⏳
                    </div>
                    <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Loading conversation...
                    </h3>
                    <p className={`text-xs sm:text-sm ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      Please wait while we connect you to the seller
                    </p>
                  </div>
                </div>
              ) : (
                <div className={`flex-1 flex items-center justify-center ${
                  isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
                }`}>
                  <div className="text-center px-6 max-w-md">
                                            {/* Modern animated icon with blue and red colors */}
                        <div className="relative mb-8">
                          <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${
                            isDarkMode 
                              ? 'bg-gradient-to-br from-blue-500/20 to-red-500/20 border border-blue-400/30' 
                              : 'bg-gradient-to-br from-blue-100 to-red-100 border border-blue-200'
                          } shadow-lg backdrop-blur-sm`}>
                            <div className={`animate-pulse ${
                              isDarkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                              <svg 
                                width="40" 
                                height="40" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-10 h-10"
                              >
                                <path 
                                  d="M8 12H8.01M12 12H12.01M16 12H16.01M21 12C21 16.9706 16.9706 21 12 21C10.4876 21 9.01589 20.6888 7.65947 20.1012L3 21L3.8988 16.3405C3.3112 14.9841 3 13.5124 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                          {/* Enhanced floating dots with blue and red colors */}
                          <div className="absolute -top-3 -right-3 w-5 h-5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full animate-bounce shadow-lg"></div>
                          <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-red-500 to-red-600 rounded-full animate-bounce shadow-lg" style={{animationDelay: '0.3s'}}></div>
                          <div className="absolute top-1/2 -right-6 w-3 h-3 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                        </div>
                        
                        {/* Modern typography with blue and red gradient */}
                        <h3 className={`text-2xl sm:text-3xl font-bold mb-4 bg-gradient-to-r ${
                          isDarkMode 
                            ? 'from-blue-400 via-red-500 to-red-400' 
                            : 'from-blue-600 via-red-500 to-red-600'
                        } bg-clip-text text-transparent`}>
                          Ready to Chat?
                        </h3>
                        
                        {/* Modern description with blue and red styling */}
                        <div className={`relative mb-8 p-4 rounded-xl ${
                          isDarkMode 
                            ? 'bg-gradient-to-r from-gray-800/50 to-gray-700/50 border border-gray-600/30' 
                            : 'bg-gradient-to-r from-blue-50 to-red-50 border border-gray-200'
                        } backdrop-blur-sm`}>
                          <p className={`text-sm sm:text-base leading-relaxed ${
                            isDarkMode ? 'text-gray-200' : 'text-gray-700'
                          }`}>
                            Select a conversation from the sidebar to start 
                            <span className={`font-semibold ${
                              isDarkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}> secure messaging </span>
                            with sellers, buyers, or our 
                            <span className={`font-semibold ${
                              isDarkMode ? 'text-red-400' : 'text-red-500'
                            }`}> dedicated support agents</span>.
                          </p>
                          {/* Decorative elements with blue and red colors */}
                          <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full opacity-60"></div>
                          <div className="absolute bottom-2 left-2 w-2 h-2 bg-red-500 rounded-full opacity-60"></div>
                        </div>
                    
                    
                  </div>
                </div>
              )}
            </div>

            {/* Product Detail removed for all devices as requested */}
          </div>
        </div>
      </div>
    </FirebaseErrorBoundary>
  );
};

export default ChatAppPopup;
